import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import { AppShell } from '@/components/layout/AppShell';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import ReportBugButton from '@/components/ReportBugButton';
import { installGlobalErrorHandlers } from '@/lib/errorReporter';
import '@/App.css';

import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import HomePage from '@/pages/HomePage';
import BlogPage from '@/pages/BlogPage';
import BlogPostPage from '@/pages/BlogPostPage';
import ContactPage from '@/pages/ContactPage';
import DashboardPage from '@/pages/DashboardPage';
import ContactsPage from '@/pages/ContactsPage';
import ProductsPage from '@/pages/ProductsPage';
import TemplatesPage from '@/pages/TemplatesPage';
import CampaignsPage from '@/pages/CampaignsPage';
import CampaignBuilderPage from '@/pages/CampaignBuilderPage';
import GatewaysPage from '@/pages/GatewaysPage';
import PhoneGatewaysPage from '@/pages/PhoneGatewaysPage';
import PromoCodesPage from '@/pages/PromoCodesPage';
import BlacklistPage from '@/pages/BlacklistPage';
import TrackingPage from '@/pages/TrackingPage';
import AIPage from '@/pages/AIPage';
import PayoutsPage from '@/pages/PayoutsPage';
import AdminPage from '@/pages/AdminPage';
import InterestsPage from '@/pages/InterestsPage';
import ToolsPage from '@/pages/ToolsPage';
import SmtpDomainsPage from '@/pages/SmtpDomainsPage';
import MailClientPage from '@/pages/MailClientPage';
import FootersPage from '@/pages/FootersPage';
import ErrorsPage from '@/pages/ErrorsPage';
import AgentPage from '@/pages/AgentPage';
import AIConfigPage from '@/pages/AIConfigPage';
import InboxPage from '@/pages/InboxPage';
import SegmentsPage from '@/pages/SegmentsPage';
import JobsPage from '@/pages/JobsPage';
import VoiceConfigPage from '@/pages/VoiceConfigPage';
import ExternalServicesPage from '@/pages/ExternalServicesPage';
import ProfilePage from '@/pages/ProfilePage';

installGlobalErrorHandlers();

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="space-y-4 w-64">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </div>
  );
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <div className="dark">
      <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <ReportBugButton />
          <Routes>
            {/* Public pages */}
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
            <Route path="/blog/:slug" element={<PublicLayout><BlogPostPage /></PublicLayout>} />
            <Route path="/kontakt" element={<PublicLayout><ContactPage /></PublicLayout>} />
            {/* Auth */}
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
            <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
            <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
            <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
            <Route path="/campaigns/new" element={<ProtectedRoute><CampaignBuilderPage /></ProtectedRoute>} />
            <Route path="/gateways" element={<ProtectedRoute><GatewaysPage /></ProtectedRoute>} />
            <Route path="/phone-gateways" element={<ProtectedRoute><PhoneGatewaysPage /></ProtectedRoute>} />
            <Route path="/external-services" element={<ProtectedRoute><ExternalServicesPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/promo-codes" element={<ProtectedRoute><PromoCodesPage /></ProtectedRoute>} />
            <Route path="/blacklist" element={<ProtectedRoute><BlacklistPage /></ProtectedRoute>} />
            <Route path="/tracking" element={<ProtectedRoute><TrackingPage /></ProtectedRoute>} />
            <Route path="/ai" element={<ProtectedRoute><AIPage /></ProtectedRoute>} />
            <Route path="/payouts" element={<ProtectedRoute><PayoutsPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
            <Route path="/interests" element={<ProtectedRoute><InterestsPage /></ProtectedRoute>} />
            <Route path="/tools" element={<ProtectedRoute><ToolsPage /></ProtectedRoute>} />
            <Route path="/smtp-domains" element={<ProtectedRoute><SmtpDomainsPage /></ProtectedRoute>} />
            <Route path="/mail" element={<ProtectedRoute><MailClientPage /></ProtectedRoute>} />
            <Route path="/email-footers" element={<ProtectedRoute><FootersPage /></ProtectedRoute>} />
            <Route path="/admin/errors" element={<ProtectedRoute><ErrorsPage /></ProtectedRoute>} />
            <Route path="/admin/agent" element={<ProtectedRoute><AgentPage /></ProtectedRoute>} />
            <Route path="/admin/ai-config" element={<ProtectedRoute><AIConfigPage /></ProtectedRoute>} />
            <Route path="/admin/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
            <Route path="/segments" element={<ProtectedRoute><SegmentsPage /></ProtectedRoute>} />
            <Route path="/admin/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
            <Route path="/admin/voice-config" element={<ProtectedRoute><VoiceConfigPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
